import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchSar } from '@/lib/weaviate/fetch-sar';

export const Route = createFileRoute('/experimental/_experimentalLayout/sar')({
  component: SARPage,
});

type SarSearchResult = {
  caseID: string;
  datum: string;
  document_text: string;
  file_name: string;
  mimetype: string;
  otId: string;
  relatie: string;
  relatie_id_naar: string;
  relatie_id_van: string;
  relatiesoort_naar: string;
  relatiesoort_van: string;
  richting: string;
  type: string;
  workpartyrol_naar: string;
  workpartyrol_van: string;
  zaaksoort: string;
  _additional: {
    certainty: number;
  };
};

function SARPage() {
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<SarSearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchText.trim()) return;

    setLoading(true);
    try {
      const data = await fetchSar(searchText);
      setResults(data as SarSearchResult[]);
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <div className="p-4">
      <Card>
        <CardContent className="flex flex-row gap-2 p-4">
          <Input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Enter search text..."
            className="flex-grow"
          />
          <Button onClick={handleSearch} disabled={loading} variant="default">
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </CardContent>
      </Card>

      <div className="mt-4 space-y-4">
        {results.map((result, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle>Case {result.caseID}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <div>{formatDate(result.datum)}</div>
                </div>
                <div className="space-y-2">
                  <Label>Zaaksoort</Label>
                  <div>{result.zaaksoort}</div>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <div>{result.type}</div>
                </div>
                <div className="space-y-2">
                  <Label>Richting</Label>
                  <div>{result.richting}</div>
                </div>
                <div className="space-y-2">
                  <Label>Relatie</Label>
                  <div>{result.relatie}</div>
                </div>
                <div className="space-y-2">
                  <Label>Bestandsnaam</Label>
                  <div>{result.file_name}</div>
                </div>
                <div className="space-y-2">
                  <Label>Certainty</Label>
                  <div>{Math.round(result._additional.certainty * 100)}%</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Document Tekst</Label>
                <Card className="bg-muted">
                  <CardContent className="p-4">
                    <pre className="whitespace-pre-wrap text-sm">{result.document_text}</pre>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Relatie Details</Label>
                  <div className="space-y-1">
                    <div>
                      Van: {result.relatiesoort_van} ({result.workpartyrol_van})
                    </div>
                    <div>
                      Naar: {result.relatiesoort_naar} ({result.workpartyrol_naar})
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Technische Details</Label>
                  <div className="space-y-1">
                    <div>MIME Type: {result.mimetype}</div>
                    <div>OT ID: {result.otId}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
